import React, { useState, useCallback, memo } from "react";

import { IEMIBankPlan, IEMITenure } from "@swiggy-private/dls-web-recipe-common-web/dist/components/EMICard";
import { EMICardWrapper } from "./EMICardWrapper";
import { EMI_LIST_TYPE_MAP, TITLE_MAP } from "./constant";
import { ParentContainer, EmiContainer, EmiGroupTitle } from "./EMIPlanList.styled";

interface EMIPlanListProps {
    emiPlans: IEMIBankPlan[];
    savedCardEmiPlans: IEMIBankPlan[];
    onProceedClick: (emiPlan: IEMIBankPlan, tenure: IEMITenure) => void;
    onEMIExpandClick: () => void;
    onEMITncClick: () => void;
    onEmiOptionClick: () => void;
}

const EMIPlanList: React.FC<EMIPlanListProps> = ({
    emiPlans = [],
    savedCardEmiPlans = [],
    onProceedClick,
    onEMIExpandClick,
    onEMITncClick,
    onEmiOptionClick,
}) => {
    const [selectedCard, setSelectedCard] = useState<string | null>(null);

    const handleCardSelection = useCallback(
        (cardId: string | null) => {
            onEmiOptionClick();
            setSelectedCard(cardId);
        },
        [onEmiOptionClick],
    );

    const handleCTAClick = useCallback(
        (emiPlan: IEMIBankPlan, tenure: IEMITenure) => {
            onProceedClick(emiPlan, tenure);
        },
        [onProceedClick],
    );

    const renderEMICards = useCallback(
        (emiList: IEMIBankPlan[], groupType: string) => {
            return emiList.map((emi, index) => {
                const cardId = `${emi.bankName}-${index}-${groupType}`;
                const showSeparator = index < emiList.length - 1;

                return (
                    <EMICardWrapper
                        key={cardId}
                        emi={emi}
                        cardId={cardId}
                        selectedCardId={selectedCard}
                        onCardSelection={handleCardSelection}
                        onCTAClick={handleCTAClick}
                        showSeparator={showSeparator}
                        onEMIExpandClick={onEMIExpandClick}
                        onEMITncClick={onEMITncClick}
                    />
                );
            });
        },
        [selectedCard, handleCardSelection, handleCTAClick, onEMIExpandClick, onEMITncClick],
    );

    const renderEMIGroup = useCallback(
        (groupType: string, emiList: IEMIBankPlan[]) => {
            const title = TITLE_MAP[groupType];

            if (emiList.length === 0) {
                return null;
            }

            return (
                <div key={`${groupType}`}>
                    {savedCardEmiPlans.length > 0 && emiPlans.length > 0 && (
                        <EmiGroupTitle variant="V2.body1.neutral.bold" color="text_Highest_Emphasis">
                            {title}
                        </EmiGroupTitle>
                    )}
                    <EmiContainer>{renderEMICards(emiList, groupType)}</EmiContainer>
                </div>
            );
        },
        [savedCardEmiPlans, emiPlans, renderEMICards],
    );

    return (
        <ParentContainer data-testid="emi-plan-list-container">
            {/* Fixed order: Saved Cards first, then Add New Cards */}
            {renderEMIGroup(EMI_LIST_TYPE_MAP.SAVED, savedCardEmiPlans)}
            {renderEMIGroup(EMI_LIST_TYPE_MAP.NEW, emiPlans)}
        </ParentContainer>
    );
};

export default memo(EMIPlanList);
